import { useQuery } from "@tanstack/react-query";
import { fetchBookingHistory } from "../../../../../APIs/Booking/GetBookingHistory";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { BookingHistoryChangeSet } from "../../../../../types/bookingHistory";
import Loading from "../../../../../utils/Loading";

const formatHistoryDate = (dateValue: string | Date): string => {
  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (isNaN(date.getTime())) return String(dateValue);

  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

interface HistoryRowProps {
  date: string | Date;
  userName: string;
  userInitials?: string;
  changes: string[];
}

const HistoryRow = ({
  date,
  userName,
  userInitials,
  changes
}: HistoryRowProps) => (
  <tr>
    <td className="HistoryTable-DateCell min-w-[10rem] whitespace-nowrap">
      {formatHistoryDate(date)}
    </td>
    <td className="HistoryTable-UserCell min-w-[8rem] whitespace-nowrap">
      {userName}
      {userInitials ? ` (${userInitials})` : ""}
    </td>
    <td className="HistoryTable-ChangeCell">
      <ul>
        {changes.map((change) => (
          <li key={change}>{change}</li>
        ))}
      </ul>
    </td>
  </tr>
);

const SelectedBookingHistory = () => {
  const { selectedBooking, selectedBookingV4 } = useDiaryContext();
  const bookingId = selectedBooking?.id;

  const {
    data: historyResponse,
    isLoading,
    isError
  } = useQuery({
    queryKey: ["GetBookingHistory", bookingId],
    queryFn: () => fetchBookingHistory(bookingId),
    enabled: !!bookingId
  });

  const history = historyResponse?.history ?? [];

  if (isLoading) {
    return (
      <div className="SelectedBookingHistory p-panel-padding h-full overflow-auto">
        <Loading text="Loading history..." />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="SelectedBookingHistory p-panel-padding h-full overflow-auto">
        <p className="SelectedBookingHistory-Error text-destructive text-center p-4">
          Failed to load booking history.
        </p>
      </div>
    );
  }

  const BOOKING_SOURCE_ADMIN = "admin";
  const createdByName =
    selectedBookingV4?.source === BOOKING_SOURCE_ADMIN ? "Staff" : "Customer";

  return (
    <div className="SelectedBookingHistory p-panel-padding h-full overflow-auto">
      <div className="SelectedBookingHistory-TableWrapper bg-background rounded-lg overflow-hidden">
        <table
          className="HistoryTable w-full border-collapse text-sm"
          role="table"
        >
          <thead>
            <tr>
              <th
                className="HistoryTable-DateHeader min-w-[10rem] whitespace-nowrap py-3 px-4 text-left font-semibold text-foreground border-b border-border-color"
                scope="col"
              >
                Date
              </th>
              <th
                className="HistoryTable-UserHeader min-w-[8rem] whitespace-nowrap py-3 px-4 text-left font-semibold text-foreground border-b border-border-color"
                scope="col"
              >
                User
              </th>
              <th
                className="HistoryTable-ChangeHeader py-3 px-4 text-left font-semibold text-foreground border-b border-border-color"
                scope="col"
              >
                Change
              </th>
            </tr>
          </thead>
          <tbody>
            {history.map((changeSet: BookingHistoryChangeSet) => (
              <HistoryRow
                key={`history-${changeSet.created_date}-${changeSet.user?.name ?? "unknown"}`}
                date={changeSet.created_date}
                userName={changeSet.user?.name ?? "Unknown"}
                userInitials={changeSet.user_initials}
                changes={Object.values(changeSet.changes).map(
                  (change) => change.description
                )}
              />
            ))}
            {selectedBookingV4?.created_date && (
              <HistoryRow
                date={
                  typeof selectedBookingV4.created_date === "string"
                    ? selectedBookingV4.created_date
                    : selectedBookingV4.created_date.toISOString()
                }
                userName={createdByName}
                changes={["Created enquiry"]}
              />
            )}
            {history.length === 0 && !selectedBookingV4?.created_date && (
              <tr>
                <td
                  colSpan={3}
                  className="HistoryTable-EmptyCell text-center text-muted-foreground py-8 px-4"
                >
                  No history found for this booking.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SelectedBookingHistory;
